import React, { useState, useRef } from 'react';
import SafeIcon from '../../common/SafeIcon';

const TemplatePreviewModal = ({ template, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [showRawHtml, setShowRawHtml] = useState(false);
  const textareaRef = useRef(null);

  const handleCopyHtml = async () => {
    const htmlContent = template.html || '';

    try {
      await navigator.clipboard.writeText(htmlContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
      return;
    } catch (err) {
      console.warn('Clipboard API failed, trying fallback:', err);
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = htmlContent;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '-9999px';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
        return;
      }
    } catch (err) {
      console.warn('execCommand fallback failed:', err);
    }

    setShowRawHtml(true);
  };

  const handleSelectAll = () => {
    if (textareaRef.current) {
      textareaRef.current.select();
      textareaRef.current.focus();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch (e) {
        // User can manually Ctrl+C
      }
    }
  };

  const htmlContent = template.html || '';
  const htmlSize = new Blob([htmlContent]).size;
  const htmlSizeKB = (htmlSize / 1024).toFixed(1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-2xl">{template.icon}</span>
              <div>
                <h3 className="font-bold text-gray-800 text-lg">{template.name}</h3>
                <p className="text-xs text-gray-400">{template.subject}</p>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {template.isSupabaseTemplate && (
                <>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1">
                    <SafeIcon name="Shield" className="text-xs" />
                    Supabase Auth Template
                  </span>
                  {template.supabaseSlot && (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">
                      Paste into: "{template.supabaseSlot}"
                    </span>
                  )}
                </>
              )}
              {template.isAppTemplate && (
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 flex items-center gap-1">
                  <SafeIcon name="Zap" className="text-xs" />
                  Sent via Emailit API
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowRawHtml(!showRawHtml)}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all border ${
                showRawHtml
                  ? 'bg-gray-100 text-gray-700 border-gray-200'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              <SafeIcon name="Code" className="text-sm" />
              {showRawHtml ? 'Hide Code' : 'View Code'}
            </button>
            <button
              onClick={handleCopyHtml}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all ${
                copied
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-md hover:shadow-lg'
              }`}
            >
              <SafeIcon name={copied ? 'Check' : 'Copy'} className="text-sm" />
              {copied ? 'Copied!' : 'Copy HTML'}
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
              <SafeIcon name="X" className="text-xl" />
            </button>
          </div>
        </div>

        {/* Supabase Auth Instructions */}
        {template.isSupabaseTemplate && !showRawHtml && (
          <div className="mx-6 mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex-shrink-0">
            <p className="text-sm font-semibold text-emerald-800 mb-2">📋 How to use this Supabase Auth template:</p>
            <ol className="text-xs text-emerald-700 space-y-1.5 list-decimal list-inside">
              <li>Click <strong>"Copy HTML"</strong> above (or "View Code" to see and manually select the HTML)</li>
              <li>Go to <strong>Supabase Dashboard → Authentication → Email Templates</strong></li>
              <li>Find the <strong>"{template.supabaseSlot}"</strong> template section</li>
              <li>Click <strong>Edit</strong> on that template, paste the HTML, and click <strong>Save</strong></li>
              <li>Ensure Site URL is <code className="bg-emerald-100 px-1.5 py-0.5 rounded font-mono">https://dayclap.com</code></li>
              {template.supabaseSlot === 'Reset password' && (
                <li>Add Redirect URL: <code className="bg-emerald-100 px-1.5 py-0.5 rounded font-mono">https://dayclap.com/reset-password</code></li>
              )}
            </ol>
            <div className="mt-3 bg-white/60 rounded-lg p-3">
              <p className="text-xs text-emerald-600 font-semibold mb-1.5">Supabase variables used (auto-filled by Supabase):</p>
              <div className="flex flex-wrap gap-1.5">
                {template.variables?.map((v) => (
                  <code key={v} className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-mono">
                    {v}
                  </code>
                ))}
              </div>
              <p className="text-xs text-emerald-500 mt-2">⚡ These variables are automatically replaced by Supabase when the email is sent. You don't need to do anything — Supabase handles it.</p>
            </div>
          </div>
        )}

        {/* App Template Instructions */}
        {template.isAppTemplate && !showRawHtml && (
          <div className="mx-6 mt-4 bg-violet-50 border border-violet-200 rounded-xl p-4 flex-shrink-0">
            <p className="text-sm font-semibold text-violet-800 mb-2">⚡ App-sent email (via Emailit API):</p>
            <p className="text-xs text-violet-700 leading-relaxed">
              This template is sent programmatically from the app via the <strong>send-email</strong> Supabase Edge Function (which calls Emailit API).
              The variables below are replaced by the edge function when sending. You can store this HTML in the <strong>email_templates</strong> database table and reference it by slug.
            </p>
            <div className="mt-3 bg-white/60 rounded-lg p-3">
              <p className="text-xs text-violet-600 font-semibold mb-1.5">Custom variables (replaced by edge function):</p>
              <div className="flex flex-wrap gap-1.5">
                {template.variables?.map((v) => (
                  <code key={v} className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded font-mono">
                    {v}
                  </code>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Raw HTML View */}
        {showRawHtml && (
          <div className="mx-6 mt-4 flex-shrink-0">
            <div className="bg-gray-900 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <span className="text-xs text-gray-400 font-mono ml-2">
                    {template.slug}.html — {htmlSizeKB} KB
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSelectAll}
                    className="text-xs text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    <SafeIcon name="Copy" className="text-xs" />
                    {copied ? 'Copied!' : 'Select All & Copy'}
                  </button>
                </div>
              </div>
              <textarea
                ref={textareaRef}
                value={htmlContent}
                readOnly
                className="w-full h-[400px] bg-gray-900 text-green-400 font-mono text-xs p-4 outline-none resize-none leading-relaxed"
                onClick={(e) => e.target.select()}
              />
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              💡 Click inside the code box and press <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-mono">Ctrl+C</kbd> (or <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-mono">Cmd+C</kbd>) to copy
            </p>
          </div>
        )}

        {/* Preview */}
        {!showRawHtml && (
          <div className="flex-1 overflow-auto p-6">
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <iframe
                srcDoc={htmlContent}
                title="Email Preview"
                className="w-full h-[600px] bg-white"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatePreviewModal;